import React, { ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { Translations, CanModify } from './configuration';
import { Typography } from '@material-ui/core';
import {
  AppTextField,
  AppSelect,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { SchemeTypes } from '../../../../config';
import { FormFields } from '../../configuration';

interface IProps {
  mode: 'VIEW' | 'EDIT';
  scheme_type: SchemeTypes;
  is_agent: boolean;
  isExcluded: boolean;
  errors: DeepMap<FormFields, FieldError>;
  control: Control<FormFields>;
  getValues: any;
  watch: any;
}

export default function PaymentInfoForm(props: IProps): ReactElement {
  // console.log('Render Payment Info Form');
  const mode = props.mode;
  const SCHEME_TYPE = props.scheme_type;
  const IS_AGENT = props.is_agent;
  const errors = props.errors;
  const control = props.control;
  const isExcluded = props.isExcluded;
  const classes = useStyles();
  const watch = props.watch;
  const watchIbanObanSelection = watch('paymentInfo_iban_oban_selection');

  return (
    <div>
      <Typography variant="h5" style={{ marginBottom: '16px' }}>
        <TranslatedText textMap={Translations.payment_info_label} />
      </Typography>
      <GridLayout
        elements={
          SCHEME_TYPE === SchemeTypes.NON_EU
            ? [
                {
                  element: renderIbanField(
                    errors,
                    control,
                    mode,
                    SCHEME_TYPE,
                    IS_AGENT,
                    isExcluded,
                    props.getValues,
                    watchIbanObanSelection
                  ),
                  size: 4,
                  id: watchIbanObanSelection === 'OBAN' ? 'oban' : 'iban',
                },
                {
                  element: renderIbanObanSelector(
                    errors,
                    control,
                    mode,
                    SCHEME_TYPE,
                    IS_AGENT,
                    isExcluded
                  ),
                  size: 2,
                  id: 'iban_oban_selection',
                },
                {
                  element: renderAccountHolderField(
                    errors,
                    control,
                    mode,
                    SCHEME_TYPE,
                    IS_AGENT,
                    isExcluded
                  ),
                  size: 4,
                  id: 'accountHolder',
                },
                {
                  element: renderBicField(
                    errors,
                    control,
                    mode,
                    SCHEME_TYPE,
                    IS_AGENT,
                    isExcluded
                  ),
                  size: 2,
                  id: 'bic',
                },
              ]
            : [
                {
                  element: renderIbanField(
                    errors,
                    control,
                    mode,
                    SCHEME_TYPE,
                    IS_AGENT,
                    isExcluded,
                    props.getValues,
                    watchIbanObanSelection
                  ),
                  size: 6,
                  id: 'iban',
                },
                {
                  element: renderAccountHolderField(
                    errors,
                    control,
                    mode,
                    SCHEME_TYPE,
                    IS_AGENT,
                    isExcluded
                  ),
                  size: 4,
                  id: 'accountHolder',
                },
                {
                  element: renderBicField(
                    errors,
                    control,
                    mode,
                    SCHEME_TYPE,
                    IS_AGENT,
                    isExcluded
                  ),
                  size: 2,
                  id: 'bic',
                },
              ]
        }
      />
    </div>
  );
}

const renderIbanField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean,
  getValues: any,
  watchIbanObanSelection: string | null
) => {
  /**
   * Returns true if invalid
   * @param value
   */
  function ValidIBAN(value: string | null) {
    const validation = new RegExp(/^[A-Z]{2}[0-9]{2}[0-9A-Z]{10,30}$/);
    let isValidIBAN = false;
    if (value != null && value.length !== 0) {
      isValidIBAN = !validation.test(value);
    }
    return isValidIBAN;
  }

  /**
   * Returns true if invalid
   * @param value
   */
  function ValidIBANCheckDigits(value: string | null) {
    var invalidIBANCheckDigits = false;

    if (value != null && value.length !== 0) {
      //clean-up IBAN from empty spaces
      var noWhiteSpaceIBAN: string = value.replace(/\s/g, '');

      //Step 1 - Move the first four characters at the end of the string
      var processedIBAN =
        noWhiteSpaceIBAN.substring(4, noWhiteSpaceIBAN.length) +
        noWhiteSpaceIBAN.substring(0, 4);

      //Step 2 - Replace the letters in IBAN with their corresponding digits, expanding the string since A=10, B=11 ... and Z=35.
      const asciiShift: number = 55;

      var checkSumString: string = '';

      Array.from(processedIBAN).forEach((character: string) => {
        var isLetter = /^[A-Z]$/i.test(character);

        checkSumString += isLetter
          ? character.charCodeAt(0) - asciiShift
          : character;
      });

      var checkSumDigit: number = Number(checkSumString.substring(0, 1));

      for (var i = 1; i < checkSumString.length; i++) {
        var v: number = Number(checkSumString.substring(i, i + 1));

        checkSumDigit *= 10;
        checkSumDigit += v;
        checkSumDigit %= 97;
      }

      invalidIBANCheckDigits = checkSumDigit !== 1;
    }
    return invalidIBANCheckDigits;
  }

  /**
   * Returns true if invalid
   * @param value
   */
  function ValidOBAN(value: string | null) {
    //const validation = new RegExp(/^[0-9A-Za-z]{1,64}$/);
    const validation = new RegExp('^[0-9]+$');
    let isValidOBAN = false;
    if (value != null && value.length !== 0) {
      isValidOBAN = !validation.test(value);
    }
    console.log(isValidOBAN);
    return isValidOBAN;
  }

  /**
   * Returns true if invalid
   * @param value
   */
  function ValidOBANCheckDigits(value: string | null) {
    var invalidOBANCheckDigits = false;

    if (value != null && value.length !== 0) {
      //clean-up OBAN from empty spaces
      var noWhiteSpaceOBAN: string = value.replace(/\s/g, '');

      console.log('empty spaces');
      console.log(noWhiteSpaceOBAN);

      //Step 1 - Move the first four characters at the end of the string
      var processedOBAN =
        noWhiteSpaceOBAN.substring(4, noWhiteSpaceOBAN.length) +
        noWhiteSpaceOBAN.substring(0, 4);

      console.log('Move the first four characters at the end of the string');
      console.log(processedOBAN);

      //Step 2 - Replace the letters in OBAN with their corresponding digits, expanding the string since A=10, B=11 ... and Z=35.
      const asciiShift: number = 55;

      var checkSumString: string = '';

      Array.from(processedOBAN).forEach((character: string) => {
        var isLetter = /^[A-Z]$/i.test(character);

        checkSumString += isLetter
          ? character.charCodeAt(0) - asciiShift
          : character;
      });

      var checkSumDigit: number = Number(checkSumString.substring(0, 1));

      for (var i = 1; i < checkSumString.length; i++) {
        var v: number = Number(checkSumString.substring(i, i + 1));

        checkSumDigit *= 10;
        checkSumDigit += v;
        checkSumDigit %= 97;
      }

      invalidOBANCheckDigits = checkSumDigit !== 1;
    }
    return invalidOBANCheckDigits;
  }

  return SCHEME_TYPE !== SchemeTypes.NON_EU ||
    watchIbanObanSelection === 'IBAN' ? (
    <AppTextField
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="paymentInfo_iban"
      label="IBAN"
      error={'paymentInfo_iban' in errors}
      name="paymentInfo_iban"
      control={control}
      rules={{
        required: 'Required Field',
        validate: () =>
          ValidIBAN(getValues('paymentInfo_iban')) ||
          ValidIBANCheckDigits(getValues('paymentInfo_iban'))
            ? 'Invalid IBAN format'
            : null,
      }}
      helperText={
        errors.paymentInfo_iban ? (
          errors.paymentInfo_iban.message
        ) : SCHEME_TYPE === SchemeTypes.NON_EU ? (
          <TranslatedText textMap={Translations.iban_help} />
        ) : (
          ' '
        )
      }
      // onBlur={onFocusOut}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('paymentInfo_iban') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  ) : watchIbanObanSelection === 'OBAN' ? (
    <AppTextField
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="paymentInfo_oban"
      label="OBAN"
      error={'paymentInfo_oban' in errors}
      name="paymentInfo_oban"
      control={control}
      rules={{
        required: 'Required Field',
        validate: () =>
          ValidOBAN(getValues('paymentInfo_oban'))
            ? 'Invalid OBAN format'
            : null,
      }}
      helperText={
        errors.paymentInfo_oban ? errors.paymentInfo_oban.message : ' '
      }
      // onBlur={onFocusOut}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('paymentInfo_oban') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  ) : (
    <h3 style={{ color: 'red' }}>
      {<TranslatedText textMap={Translations.no_iban_oban_selected} />}
    </h3>
  );
};

const renderIbanObanSelector = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  if (SCHEME_TYPE !== SchemeTypes.NON_EU) {
    return <div></div>;
  }

  const options = ['IBAN', 'OBAN'];
  const select_options = [];
  select_options.push({
    id: null,
    label: Translations.iban_control.English,
  });

  options.forEach((option) => {
    var obj = {
      id: `${option}`,
      label: `${option}`,
    };
    select_options.push(obj);
  });

  return (
    <AppSelect
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="paymentInfo_iban_oban_selection"
      label={<TranslatedText textMap={Translations.iban_control} />}
      error={'paymentInfo_iban_oban_selection' in errors}
      name="paymentInfo_iban_oban_selection"
      control={control}
      rules={{ required: 'Required Field' }}
      options={select_options as any}
      helperText={
        errors.paymentInfo_iban_oban_selection &&
        errors.paymentInfo_iban_oban_selection.message
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('paymentInfo_iban') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderAccountHolderField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      // margin="normal"
      required
      fullWidth
      id="paymentInfo_accountHolder"
      label={<TranslatedText textMap={Translations.account_holder_label} />}
      error={'paymentInfo_accountHolder' in errors}
      name="paymentInfo_accountHolder"
      control={control}
      rules={{
        required: 'Required Field',
        maxLength: {
          value: 70,
          message: 'Maximum Account Holder length 70 characters',
        },
        pattern: {
          value: /^[A-Za-z0-9/\-?:().,'+ ]*$/,
          message: 'Invalid Account holder format',
        },
      }}
      helperText={
        errors.paymentInfo_accountHolder
          ? errors.paymentInfo_accountHolder.message
          : ' '
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'paymentInfo_accountHolder'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderBicField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      required
      fullWidth
      id="paymentInfo_bic"
      label="BIC"
      error={'paymentInfo_bic' in errors}
      name="paymentInfo_bic"
      control={control}
      rules={{
        required: 'Required Field',
        pattern: {
          value: /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3}){0,1}$/,
          message: 'Invalid BIC format',
        },
      }}
      helperText={errors.paymentInfo_bic ? errors.paymentInfo_bic.message : ' '}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('paymentInfo_bic') ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};
